import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminSettings from './components/AdminSettings';
import EngineImpactPanel from './components/EngineImpactPanel';


const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;
const API_FALLBACK = `/api`;

const AdminDashboard = ({ onBack, onLogout }) => {
  // Admin authentication state - use regular JWT token instead of separate admin_token
  // Initialize adminToken from existing auth token and auto-authenticate
  const [adminToken, setAdminToken] = useState(localStorage.getItem('token'));
  const [isAuthenticated, setIsAuthenticated] = useState(true); // Trust main app authentication

  // Dashboard state
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState('desc');
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  // Safe helpers for IDs
  const safeShortId = (user) => {
    try {
      const uid = (user && (user.user_id || user._id)) ? String(user.user_id || user._id) : '';
      return uid ? uid.slice(0, 8) : 'unknown';
    } catch (e) {
      return 'unknown';
    }
  };
  const hasUserId = (user) => Boolean(user && (user.user_id || user._id));

  const [selectedUser, setSelectedUser] = useState(null);

  // Pagination and search
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPagination, setUsersPagination] = useState(null);
  
  // Support ticket management state
  const [supportTickets, setSupportTickets] = useState([]);
  const [supportStats, setSupportStats] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketFilter, setTicketFilter] = useState('open'); // open, in_progress, resolved, closed, all
  
  // Chat-based ticket state
  const [ticketMessages, setTicketMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  
  // Pagination state
  const [ticketPage, setTicketPage] = useState(1);
  const [ticketsPerPage] = useState(10);
  const [totalTickets, setTotalTickets] = useState(0);

  // Toast notification state
  const [notification, setNotification] = useState(null);

  // Email processing state
  const [emailProcessing, setEmailProcessing] = useState(false);
  const [unreadTicketsCount, setUnreadTicketsCount] = useState(0);
  const [lastEmailCheck, setLastEmailCheck] = useState(null);
  
  // System Status State
  const [systemOverview, setSystemOverview] = useState(null);
  const [systemLoading, setSystemLoading] = useState(false);
  
  // State for delete confirmation modal
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);



  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000); // Auto dismiss after 4 seconds
  };



  useEffect(() => {
    // Skip authentication check - trust main app that user is already admin
    // Just load the dashboard data directly
    const token = localStorage.getItem('token');
    if (token) {
      setAdminToken(token);
      loadDashboardData();
    }
  }, []);

  // No separate admin login needed - user is already authenticated

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Missing auth token. Please log in again.');
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Load dashboard stats
      // Try primary backend, fallback to same-origin /api if needed
      let statsResponse, analyticsResponse;
      try {
        [statsResponse, analyticsResponse] = await Promise.all([
          axios.get(`${API}/admin/dashboard/stats`, { headers }),
          axios.get(`${API}/admin/analytics`, { headers })
        ]);
      } catch (primaryErr) {
        console.warn('Primary admin stats load failed, trying fallback /api ...', primaryErr?.message || primaryErr);
        [statsResponse, analyticsResponse] = await Promise.all([
          axios.get(`${API_FALLBACK}/admin/dashboard/stats`, { headers }),
          axios.get(`${API_FALLBACK}/admin/analytics`, { headers })
        ]);
      }

      setStats(statsResponse.data.stats);
      setAnalytics(analyticsResponse.data.analytics);

    } catch (error) {
      console.error('Error loading dashboard data:', error);
      // STOP AUTO-LOGOUT - Just show error to user for diagnosis
      const status = error.response?.status;
      const detail = error.response?.data?.detail || error.message;
      setError(`Failed to load dashboard data (Status: ${status}): ${detail}`);
      
      if (status === 401) {
         // Optionally prompt for re-login inside the dashboard, but DON'T redirect
         console.warn("Admin token invalid or expired");
      }
    } finally {
      setLoading(false);
    }
  };

  const loadSystemStatus = async () => {
    try {
      setSystemLoading(true);
      
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Missing auth token. Please log in again.');
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Load all system status data
      let overviewResponse;
      try {
        [overviewResponse] = await Promise.all([
          axios.get(`${API}/admin/system/overview`, { headers })
        ]);
      } catch (primaryErr) {

        console.warn('Primary system status failed, trying fallback /api ...', primaryErr?.message || primaryErr);
        overviewResponse = await axios.get(`${API_FALLBACK}/admin/system/overview`, { headers });
      }

      setSystemOverview(overviewResponse.data);

    } catch (error) {
      console.error('Error loading system status:', error);
      // STOP AUTO-LOGOUT
      setError(`Failed to load system status: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSystemLoading(false);
    }
  };
  // Live polling for System Overview
  const SYSTEM_POLL_MS = 8000; // 8s to reduce pressure on backend
  const livePollRef = React.useRef({ timer: null, running: false });
  const lastDiagRef = React.useRef(null);

  function ensureMonitorRunning() {
    if (!adminToken) return Promise.resolve();
    return (async () => {
      try {
        const headers = { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' };
        // Check monitor status; auto-start if stopped
        let statusResp;
        try {
          statusResp = await axios.get(`${API_FALLBACK}/monitor/status`, { headers, timeout: 4000 });
        } catch (e1) {
          // try primary API as fallback
          statusResp = await axios.get(`${API}/monitor/status`, { headers, timeout: 4000 });
        }
        if (!statusResp?.data?.running) {
          try {
            await axios.post(`${API_FALLBACK}/monitor/start`, { interval_sec: 60 }, { headers, timeout: 4000 });
          } catch (e2) {
            await axios.post(`${API}/monitor/start`, { interval_sec: 60 }, { headers, timeout: 4000 });
          }
        }
      } catch (e) {
        // No-op if cannot determine status; do not block polling
      }
    })();
  }

  const startLiveSystemPolling = () => {
    // Avoid duplicate timers
    if (livePollRef.current.running) return;
    livePollRef.current.running = true;
    const ctrl = new AbortController();

    const tick = async () => {
      if (activeTab !== 'system') return; // pause when not on tab
      try {
        // no-cache to bypass 5s microcache for freshness or include no-cache header
        const headers = {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        };
        // Pull /monitor/diag for system metrics (CPU, memory, disk) and anything else
        let diagResp;
        console.log('[SystemPolling] Fetching /monitor/diag ...');
        try {
          diagResp = await axios.get(`${API_FALLBACK}/monitor/diag`, { headers, timeout: 5000 });
        } catch (e1) {
          try {
            diagResp = await axios.get(`${API}/monitor/diag`, { headers, timeout: 5000 });
          } catch (e2) {
            // Try without auth header as a last resort (endpoint is public in most setups)
            try {
              const headers2 = { 'Content-Type': 'application/json', 'Cache-Control': 'no-cache' };
              diagResp = await axios.get(`${API_FALLBACK}/monitor/diag`, { headers: headers2, timeout: 5000 });
            } catch (e3) {
              throw e2;
            }
          }
        }
        const diag = diagResp.data || {};
        lastDiagRef.current = diag;
        // Map to current UI object if available
        // If your backend has /admin/system/overview, we keep it in sync using diag payload when present
        setSystemOverview(prev => {
          const nextHealth = {
            overall_status: (diag.db_ok === false) ? 'DEGRADED' : 'HEALTHY',
            cpu_usage: Number.isFinite(diag.cpu_pct)
              ? Math.round(diag.cpu_pct)
              : (Number.isFinite(diag.cpu?.percent)
                  ? Math.round(diag.cpu.percent)
                  : (diag.cpu_load?.['1m']
                      ? Math.round((diag.cpu_load['1m'] / (diag.cpu_load['1m'] + 1)) * 100)
                      : (prev?.system_health?.cpu_usage || 0))),
            memory_usage: Number.isFinite(diag.memory_pct)
              ? Math.round(diag.memory_pct)
              : (Number.isFinite(diag.memory?.percent)
                  ? Math.round(diag.memory.percent)
                  : (prev?.system_health?.memory_usage || 0)),
            disk_usage: Number.isFinite(diag.disk_pct)
              ? Math.round(diag.disk_pct)
              : (Number.isFinite(diag.disk?.pct)
                  ? Math.round(diag.disk.pct)
                  : (prev?.system_health?.disk_usage || 0))
          };
          return { ...(prev || {}), system_health: nextHealth };
        });
      } catch (e) {
        // soft fail, keep previous
      }
    };

    // Ensure monitor is running then kick off polling
    ensureMonitorRunning().finally(() => {
      tick();
      if (livePollRef.current.timer) clearInterval(livePollRef.current.timer);
      livePollRef.current.timer = setInterval(tick, SYSTEM_POLL_MS);
    });

    // Cleanup on tab switch/unmount
    const stop = () => {
      if (livePollRef.current.timer) {
        clearInterval(livePollRef.current.timer);
        livePollRef.current.timer = null;
      }
      livePollRef.current.running = false;
      try {
        ctrl.abort();
      } catch (_) {
        // ignore
      }
    };

    // Attach stop to window focus/visibility changes for robustness
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') stop();
    };
    document.addEventListener('visibilitychange', onVisibility);

    // return a stopper for manual call
    startLiveSystemPolling.stop = stop;
  };

  // Live polling: start/stop based on active tab and visibility
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && activeTab === 'system') {
        startLiveSystemPolling();
      } else if (startLiveSystemPolling.stop) {
        startLiveSystemPolling.stop();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    if (activeTab === 'system') {
      startLiveSystemPolling();
    }

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      if (startLiveSystemPolling.stop) startLiveSystemPolling.stop();
    };
  }, [activeTab]);

  const loadUsers = async (page = 1, search = '', sortByArg, sortDirArg) => {
    try {
      setLoading(true);

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Missing auth token. Please log in again.');
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const effSortBy = sortByArg !== undefined ? sortByArg : sortBy;
      const effSortDir = sortDirArg !== undefined ? sortDirArg : sortDir;
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '15'
      });
      if (effSortBy) {
        params.append('sort_by', effSortBy);
        params.append('sort_dir', effSortDir);
      }

      if (search) {
        params.append('search', search);
      }

      let response;
      try {
        response = await axios.get(`${API}/admin/users?${params}`, { headers });
      } catch (primaryErr) {
        console.warn('Primary users load failed, trying fallback /api ...');
        response = await axios.get(`${API_FALLBACK}/admin/users?${params}`, { headers });
      }
      
      // Privacy: DO NOT log raw user payloads or emails in console
      if (response.data.status === 'success') {
        const usersData = response.data.users;
        // Only log counts in dev mode, and never PII
        if (process.env.NODE_ENV !== 'production') {
          console.log(`[Users] Loaded ${Array.isArray(usersData) ? usersData.length : 0} users`);
        }
        setUsers(usersData);
        setUsersPagination(response.data.pagination);
        setUsersTotal(response.data.pagination.total);
      }

    } catch (error) {
      console.error('Error loading users:', error);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  // Click a column header to sort server-side (across ALL users, then paginate).
  // First click → desc, second → asc, third → clears back to default (newest).
  const handleSort = (col) => {
    let nextBy = col;
    let nextDir = 'desc';
    if (sortBy === col) {
      if (sortDir === 'desc') {
        nextDir = 'asc';
      } else {
        nextBy = null; // third click resets to default order
        nextDir = 'desc';
      }
    }
    setSortBy(nextBy);
    setSortDir(nextDir);
    setCurrentPage(1);
    loadUsers(1, searchTerm, nextBy, nextDir);
  };

  // Sortable column header with an active-direction caret.
  const SortHeader = ({ label, col }) => {
    const active = sortBy === col;
    const caret = !active ? '↕' : sortDir === 'desc' ? '↓' : '↑';
    return (
      <button
        type="button"
        onClick={() => handleSort(col)}
        data-testid={`users-sort-${col}`}
        className={`flex items-center gap-1 font-semibold transition-colors hover:text-white ${active ? 'text-blue-300' : 'text-gray-200'}`}
        title={`Sort by ${label}`}
      >
        {label}
        <span className={`text-xs ${active ? 'text-blue-300' : 'text-gray-500'}`}>{caret}</span>
      </button>
    );
  };


  const updateUserStatus = async (userId, active) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Missing auth token. Please log in again.');
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      try {
        await axios.post(`${API}/admin/users/${userId}/status`, { active }, { headers });
      } catch (primaryErr) {
        console.warn('Primary status update failed, trying fallback /api ...');
        await axios.post(`${API_FALLBACK}/admin/users/${userId}/status`, { active }, { headers });
      }
      
      // Reload users
      loadUsers(currentPage, searchTerm);
      
    } catch (error) {
      console.error('Error updating user status:', error);
      setError('Failed to update user status');
    }
  };

  const updateUserSubscription = async (userId, active, expiresAt = null) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Missing auth token. Please log in again.');
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const data = { active };
      if (expiresAt) {
        data.expires_at = expiresAt;
      }

      try {
        await axios.post(`${API}/admin/users/${userId}/subscription`, data, { headers });
      } catch (primaryErr) {
        console.warn('Primary subscription update failed, trying fallback /api ...');
        await axios.post(`${API_FALLBACK}/admin/users/${userId}/subscription`, data, { headers });
      }
      
      // Reload users
      loadUsers(currentPage, searchTerm);
      
    } catch (error) {
      console.error('Error updating user subscription:', error);
      setError('Failed to update user subscription');
    }
  };

  // Streamlined delete - stay on Users tab after delete
  const handleDeleteUser = async (userId, userEmail) => {
    // Privacy: no PII in logs

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showNotification('No auth token - please refresh page', 'error');
        return;
      }

      const response = await fetch(`${API}/admin/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (response.ok && result.status === 'success') {
        showNotification(`User ${userEmail} deleted successfully`, 'success');
        
        // Refresh just the users list - no full page reload
        loadUsers(currentPage, searchTerm);
        
        // Also refresh dashboard stats to update counts
        loadDashboardData();
        
      } else {
        showNotification(`Delete failed: ${result.message || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      showNotification(`Delete error: ${error.message}`, 'error');
    }
  };

  // Chat-based ticket functions
  const loadTicketMessages = async (ticketId) => {
    setChatLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${API}/admin/support/tickets/${ticketId}/messages`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTicketMessages(data.messages || []);
        
        // Update selected ticket with latest info
        if (data.ticket_info) {
          setSelectedTicket(prev => ({
            ...prev,
            ...data.ticket_info
          }));
        }
      } else {
        showNotification('Failed to load ticket messages', 'error');
      }
    } catch (error) {
      console.error('Error loading ticket messages:', error);
      showNotification('Error loading messages', 'error');
    } finally {
      setChatLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || sendingMessage || !selectedTicket) return;

    setSendingMessage(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${API}/admin/support/tickets/${selectedTicket.ticket_id}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: newMessage.trim()
        })
      });

      if (response.ok) {
        setNewMessage('');
        await loadTicketMessages(selectedTicket.ticket_id);
        showNotification('Message sent successfully', 'success');
      } else {
        const error = await response.json();
        showNotification(`Failed to send message: ${error.detail || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      showNotification('Error sending message', 'error');
    } finally {
      setSendingMessage(false);
    }
  };

  const openTicketChat = async (ticket) => {
    setSelectedTicket(ticket);
    try {
      await loadTicketMessages(ticket.ticket_id);
    } catch (error) {
      console.error('loadTicketMessages failed:', error);
    }

    // Mark ticket as read if it has new replies
    if (ticket.has_new_replies) {
      await markTicketAsRead(ticket.ticket_id);
    }
  };

  const closeTicketChat = () => {
    setSelectedTicket(null);
    setTicketMessages([]);
    setNewMessage('');
  };

  const handleFilterChange = (newFilter) => {
    setTicketFilter(newFilter);
    setTicketPage(1); // Reset to first page when filter changes
    loadSupportTickets(newFilter, 1);
  };

  const handlePageChange = (newPage) => {
    loadSupportTickets(ticketFilter, newPage);
  };

  // Simple cleanup function  
  // Streamlined cleanup function - remove confirmation  
  const handleCleanupInactive = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showNotification('No auth token - please refresh page', 'error');
        return;
      }

      const response = await fetch(`${API}/admin/cleanup/inactive-accounts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ days_inactive: 90 })
      });

      const result = await response.json();

      if (response.ok) {
        showNotification(`Cleanup completed: ${result.message || 'Done'}`, 'success');
        loadUsers(currentPage, searchTerm);
        loadDashboardData();
      } else {
        showNotification(`Cleanup failed: ${result.message || 'Error'}`, 'error');
      }
    } catch (error) {
      showNotification(`Cleanup error: ${error.message}`, 'error');
      console.error('Cleanup error:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Support ticket management functions
  const loadSupportTickets = async (filter = 'open', page = 1) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${API}/admin/support/tickets`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        let allTickets = data.tickets || [];
        let filteredTickets = allTickets;
        
        // Filter tickets by status
        if (filter !== 'all') {
          filteredTickets = allTickets.filter(ticket => ticket.status === filter);
        }
        
        // Calculate pagination
        const filteredCount = filteredTickets.length;
        const startIndex = (page - 1) * ticketsPerPage;
        const endIndex = startIndex + ticketsPerPage;
        const paginatedTickets = filteredTickets.slice(startIndex, endIndex);
        
        setSupportTickets(paginatedTickets);
        setTotalTickets(allTickets.length); // Always use total count, not filtered count
        setTicketPage(page);
      }
    } catch (error) {
      console.error('Error loading support tickets:', error);
    }
  };

  const loadSupportStats = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${API}/admin/support/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const stats = await response.json();
        setSupportStats(stats);
      }
    } catch (error) {
      console.error('Error loading support stats:', error);
    }
  };

  const updateTicketStatus = async (ticketId, status, adminNotes = '') => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${API}/admin/support/ticket/${ticketId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status, admin_notes: adminNotes })
      });

      if (response.ok) {
        showNotification(`Ticket ${ticketId} updated to ${status}`, 'success');
        loadSupportTickets(ticketFilter);
        loadSupportStats();
        setSelectedTicket(null);
      } else {
        const error = await response.json();
        showNotification(`Update failed: ${error.detail || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      showNotification(`Update error: ${error.message}`, 'error');
    }
  };

  const deleteSupportTicket = async (ticketId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${API}/admin/support/tickets/${ticketId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        showNotification(`Ticket ${ticketId} deleted successfully`, 'success');
        loadSupportTickets(ticketFilter);
        loadSupportStats();
        setSelectedTicket(null);
        setDeleteConfirmation(null);
      } else {
        const error = await response.json();
        showNotification(`Delete failed: ${error.detail || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      showNotification(`Delete error: ${error.message}`, 'error');
    }
  };

  const confirmDelete = (ticketId) => {
    setDeleteConfirmation({
      ticketId,
      message: `Are you sure you want to delete support ticket ${ticketId}? This action cannot be undone.`
    });
  };

  const confirmDeleteUser = (userId, userEmail) => {
    setDeleteConfirmation({
      ticketId: userId, // Reuse the same modal state
      userEmail: userEmail, // Store email for the deletion
      isUser: true, // Flag to identify this is user deletion
      message: `Are you sure you want to delete user "${userEmail}"? This action cannot be undone and will permanently remove all user data, subscription information, and trading history.`
    });
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirmation) {
      if (deleteConfirmation.isUser) {
        // Handle user deletion
        handleDeleteUser(deleteConfirmation.ticketId, deleteConfirmation.userEmail);
      } else {
        // Handle support ticket deletion  
        deleteSupportTicket(deleteConfirmation.ticketId);
      }
      // Clear the confirmation modal after action
      setDeleteConfirmation(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmation(null);
  };

  const formatTicketDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Email processing functions
  const processEmails = async () => {
    try {
      setEmailProcessing(true);
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${API}/admin/support/process-emails`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        setLastEmailCheck(new Date());
        showNotification('Email processing completed', 'success');
        
        // Reload tickets after processing emails
        loadSupportTickets(ticketFilter);
        getUnreadCount();
      } else {
        const error = await response.json();
        showNotification(`Email processing failed: ${error.detail || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      showNotification(`Email processing error: ${error.message}`, 'error');
    } finally {
      setEmailProcessing(false);
    }
  };

  const getUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${API}/admin/support/unread-count`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        setUnreadTicketsCount(result.unread_count || 0);
      }
    } catch (error) {
      console.error('Error getting unread count:', error);
    }
  };

  const markTicketAsRead = async (ticketId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${API}/admin/support/tickets/${ticketId}/mark-read`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Reload tickets and update unread count
        loadSupportTickets(ticketFilter);
        getUnreadCount();
      }
    } catch (error) {
      console.error('Error marking ticket as read:', error);
    }
  };

  // Admin access check
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-gray-800 rounded-lg p-8 shadow-xl">
            <div className="text-center">
              <div className="text-4xl mb-4">⏳</div>
              <h1 className="text-2xl font-bold mb-2">Checking Admin Access</h1>
              <p className="text-gray-400 mb-6">Verifying admin privileges...</p>
              
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main admin dashboard
  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Notification Toast */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 max-w-sm w-full bg-white rounded-lg shadow-lg border-l-4 ${
          notification.type === 'success' ? 'border-green-500' : 'border-red-500'
        }`}>
          <div className="p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                {notification.type === 'success' ? (
                  <span className="text-green-500 text-xl">✅</span>
                ) : (
                  <span className="text-red-500 text-xl">❌</span>
                )}
              </div>
              <div className="ml-3 w-0 flex-1">
                <p className={`text-sm font-medium ${
                  notification.type === 'success' ? 'text-green-800' : 'text-red-800'
                }`}>
                  {notification.message}
                </p>
              </div>
              <div className="ml-4 flex-shrink-0 flex">
                <button
                  onClick={() => setNotification(null)}
                  className="inline-flex text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Close</span>
                  <span className="text-xl">×</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Header */}
      <div className="bg-gray-800 p-4 md:p-6 border-b border-gray-700">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
            <div className="w-full sm:w-auto">
              <h1 className="text-2xl md:text-3xl font-bold mb-2 flex items-center">
                <span className="text-red-400 mr-3">👑</span>
                Admin Dashboard
              </h1>
              <p className="text-gray-400 text-sm md:text-base">Grid Trading Bot Administration</p>
            </div>
            <div className="flex items-center space-x-2 mt-4 sm:mt-0 w-full sm:w-auto">
              <button
                onClick={() => {
                  if (onLogout) onLogout();
                }}
                className="bg-red-600 hover:bg-red-700 px-3 md:px-4 py-2 rounded font-semibold transition-colors text-sm md:text-base w-auto"
                data-testid="admin-logout-button"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex space-x-2 md:space-x-8 overflow-x-auto pb-2 md:pb-0">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: '📊' },
              { id: 'system', label: 'System Status', icon: '🔧' },
              { id: 'users', label: 'Users', icon: '👥' },

              { id: 'support', label: 'Support', icon: '🎫' },
              { id: 'analytics', label: 'Analytics', icon: '📈' },
              { id: 'maintenance', label: 'Maintenance', icon: '⚙️' }
            ].map((tab) => (
              <button
                key={tab.id}
                data-testid={`admin-tab-${tab.id}`}
                onClick={() => {
                  setActiveTab(tab.id);
                  if (tab.id === 'users') {
                    loadUsers(1, '');
                    setCurrentPage(1);
                    setSearchTerm('');
                  } else if (tab.id === 'system') {
                    // Load system status when system tab is clicked
                    loadSystemStatus();
                  } else if (tab.id === 'support') {
                    // Process emails when accessing support tab
                    processEmails();
                    loadSupportTickets(ticketFilter);
                    loadSupportStats();
                    getUnreadCount();
                  }
                }}
                className={`py-3 md:py-4 px-2 md:px-3 border-b-2 transition-colors whitespace-nowrap text-sm md:text-base ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-gray-400 hover:text-white'
                }`}
              >
                <span className="mr-1 md:mr-2">{tab.icon}</span>
                <span className="hidden sm:inline">{tab.label}</span>
                {/* Show unread count badge for support tab */}
                {tab.id === 'support' && unreadTicketsCount > 0 && (
                  <span className="ml-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                    {unreadTicketsCount}
                  </span>
                )}
                {/* Show email processing indicator */}
                {tab.id === 'support' && emailProcessing && (
                  <span className="ml-2 text-yellow-400 animate-spin">⟳</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <div className="bg-red-900/20 border border-red-500 text-red-400 px-4 py-3 rounded">
            <div className="flex items-center">
              <span className="text-red-500 mr-2">⚠️</span>
              {error}
              <button
                onClick={() => setError('')}
                className="ml-auto text-red-500 hover:text-red-400"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        {loading && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-400">Loading...</p>
          </div>
        )}

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && stats && (
          <div className="space-y-6">
            <h2 className="text-2xl font-semibold mb-4">Overview</h2>
            
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-gray-800 p-6 rounded-lg">
                <div className="text-3xl mb-2">👥</div>
                <div className="text-2xl font-bold text-blue-400">{stats.total_users}</div>
                <div className="text-gray-400">Total Users</div>
              </div>
              
              <div className="bg-gray-800 p-6 rounded-lg">
                <div className="text-3xl mb-2">💳</div>
                <div className="text-2xl font-bold text-green-400">{stats.active_subscriptions}</div>
                <div className="text-gray-400">Active Subscriptions</div>
              </div>
              
              <div className="bg-gray-800 p-6 rounded-lg">
                <div className="text-3xl mb-2">💰</div>
                <div className="text-2xl font-bold text-yellow-400">{formatCurrency(stats.total_revenue_30d)}</div>
                <div className="text-gray-400">Revenue (30d)</div>
              </div>
              
              <div className="bg-gray-800 p-6 rounded-lg">
                <div className="text-3xl mb-2">📈</div>
                <div className="text-2xl font-bold text-purple-400">{stats.conversion_rate}%</div>
                <div className="text-gray-400">Conversion Rate</div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-gray-800 p-6 rounded-lg">
                <h3 className="text-xl font-semibold mb-4">New Users</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Last 7 days:</span>
                    <span className="text-white font-semibold">{stats.new_users_7d}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Last 30 days:</span>
                    <span className="text-white font-semibold">{stats.new_users_30d}</span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-800 p-6 rounded-lg">
                <h3 className="text-xl font-semibold mb-4">Subscriptions</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Active:</span>
                    <span className="text-green-400 font-semibold">{stats.active_subscriptions}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Expired:</span>
                    <span className="text-red-400 font-semibold">{stats.expired_subscriptions}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">

            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-semibold">User Management</h2>
              <div className="flex items-center space-x-4">
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  data-testid="admin-users-search-input"
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    if (e.target.value === '' || e.target.value.length > 2) {
                      loadUsers(1, e.target.value);
                      setCurrentPage(1);
                    }
                  }}
                  className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white focus:outline-none focus:border-blue-500"
                />
                <div className="text-gray-400">
                  {usersTotal} total users
                </div>
              </div>
            </div>

            {/* Users Table - Desktop */}
            <div className="bg-gray-800 rounded-lg overflow-hidden hidden md:block">
              <table className="w-full">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="text-left py-3 px-4">
                      <SortHeader label="User" col="email" />
                    </th>
                    <th className="text-left py-3 px-4">
                      <SortHeader label="Subscription" col="subscription" />
                    </th>
                    <th className="text-left py-3 px-4">
                      <SortHeader label="Status" col="status" />
                    </th>
                    <th className="text-left py-3 px-4">
                      <SortHeader label="Onboarding" col="onboarding" />
                    </th>
                    <th className="text-left py-3 px-4">
                      <SortHeader label="Joined" col="joined" />
                    </th>
                    <th className="text-left py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user, idx) => (
                    <tr key={hasUserId(user) ? (user.user_id || user._id) : `row-${idx}`} className="border-t border-gray-700">
                      <td className="py-3 px-4">
                        <div>
                          <div className="font-semibold">{user.email}</div>
                          <div className="text-sm text-gray-400">{safeShortId(user)}...</div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        {user.is_admin || user.admin_role ? (
                          <span className="px-2 py-1 rounded text-sm bg-purple-600 text-white">
                            Admin Access
                          </span>
                        ) : (
                          <span className={`px-2 py-1 rounded text-sm ${
                            user.subscription?.active 
                              ? 'bg-green-600 text-white' 
                              : 'bg-red-600 text-white'
                          }`}>
                            {user.subscription?.active ? 'Active' : 'Inactive'}
                          </span>
                        )}
                        {user.subscription?.active && !user.is_admin && !user.admin_role && (
                          <div className="text-xs text-gray-400 mt-1">
                            {user.subscription.days_remaining} days left
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded text-sm ${
                          user.active ? 'bg-blue-600 text-white' : 'bg-gray-600 text-gray-300'
                        }`}>
                          {user.active ? 'Active' : 'Disabled'}
                        </span>
                      </td>
                      <td className="py-3 px-4" data-testid={`onboarding-${user.user_id || idx}`}>
                        {user.is_admin || user.admin_role ? (
                          <span className="text-xs text-gray-500">—</span>
                        ) : (
                          <div className="flex flex-col gap-1 text-xs">
                            <span
                              className={`px-1.5 py-0.5 rounded inline-flex items-center gap-1 w-fit ${
                                user.onboarding?.email_verified
                                  ? 'bg-green-700 text-green-100'
                                  : 'bg-yellow-700 text-yellow-100'
                              }`}
                              title={user.onboarding?.email_verified
                                ? 'User clicked the SendGrid verification link'
                                : 'Has NOT verified their email — cannot log in yet'}
                            >
                              {user.onboarding?.email_verified ? '✓' : '○'} Email
                            </span>
                            <span
                              className={`px-1.5 py-0.5 rounded inline-flex items-center gap-1 w-fit ${
                                user.onboarding?.hl_configured
                                  ? 'bg-green-700 text-green-100'
                                  : 'bg-gray-700 text-gray-300'
                              }`}
                              title={user.onboarding?.hl_configured
                                ? 'Hyperliquid wallet + API key configured'
                                : 'Has NOT added HL credentials yet'}
                            >
                              {user.onboarding?.hl_configured ? '✓' : '○'} HL Keys
                            </span>
                            <span
                              className={`px-1.5 py-0.5 rounded inline-flex items-center gap-1 w-fit ${
                                user.onboarding?.builder_approved
                                  ? 'bg-green-700 text-green-100'
                                  : 'bg-gray-700 text-gray-300'
                              }`}
                              title={user.onboarding?.builder_approved
                                ? 'On-chain builder-fee approval present'
                                : 'Builder code NOT approved yet — no fee revenue from this user'}
                            >
                              {user.onboarding?.builder_approved ? '✓' : '○'} Builder
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-sm">{formatDate(user.created_at)}</div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex space-x-2">
                          {/* Account Enable/Disable - ONLY for non-admin users */}
                          {!user.is_admin && !user.admin_role && (
                            <button
                              onClick={() => updateUserStatus(user.user_id, !user.active)}
                              className={`px-3 py-1 rounded text-sm ${
                                user.active 
                                  ? 'bg-red-600 hover:bg-red-700' 
                                  : 'bg-green-600 hover:bg-green-700'
                              }`}
                              title={user.active ? "Disable user account" : "Enable user account"}
                            >
                              {user.active ? 'Disable Account' : 'Enable Account'}
                            </button>
                          )}
                          
                          {/* Subscription Management - Only for non-admin users */}
                          {!user.is_admin && !user.admin_role && (
                            <button
                              onClick={() => updateUserSubscription(
                                user.user_id, 
                                !user.subscription?.active,
                                user.subscription?.active ? null : new Date(Date.now() + 30*24*60*60*1000).toISOString()
                              )}
                              className={`px-3 py-1 rounded text-sm ${
                                user.subscription?.active 
                                  ? 'bg-yellow-600 hover:bg-yellow-700' 
                                  : 'bg-blue-600 hover:bg-blue-700'
                              }`}
                              title={user.subscription?.active ? "Cancel subscription" : "Grant subscription"}
                            >
                              {user.subscription?.active ? 'Cancel Sub' : 'Grant Sub'}
                            </button>
                          )}
                          
                          {/* Admin indicator - For admin users */}
                          {(user.is_admin || user.admin_role) && (
                            <span className="px-3 py-1 rounded text-sm bg-purple-600 text-white">
                              Admin - Protected Account
                            </span>
                          )}
                          
                          {/* DELETE USER BUTTON - WITH CONFIRMATION */}
                          {!user.is_admin && !user.admin_role && (
                            <button
                              className="px-3 py-1 rounded text-sm bg-red-700 hover:bg-red-800 text-white"
                              onClick={() => confirmDeleteUser(user.user_id, user.email)}
                            >
                              Delete User
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Users Cards - Mobile */}
            <div className="md:hidden space-y-4">
              {users.map((user, idx) => (
                <div key={hasUserId(user) ? (user.user_id || user._id) : `card-${idx}`} className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                  {/* User Header */}
                  <div className="mb-4">
                    <div className="font-medium text-white text-lg break-all">{user.email}</div>
                    <div className="text-sm text-gray-400 mt-1">ID: {safeShortId(user)}...</div>
                    <div className="text-sm text-gray-400">Joined: {formatDate(user.created_at)}</div>
                  </div>
                  
                  {/* Status Grid */}
                  <div className="grid grid-cols-1 gap-3 mb-4">
                    <div>
                      <div className="text-xs text-gray-400 mb-1">Account Status</div>
                      <span className={`px-2 py-1 rounded text-sm ${
                        user.active ? 'bg-blue-600 text-white' : 'bg-gray-600 text-gray-300'
                      }`}>
                        {user.active ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    
                    <div>
                      <div className="text-xs text-gray-400 mb-1">Subscription</div>
                      {(user.is_admin || user.admin_role) ? (
                        <span className="px-2 py-1 rounded text-sm bg-purple-600 text-white">
                          Admin Access
                        </span>
                      ) : user.subscription?.active ? (
                        <span className="px-2 py-1 rounded text-sm bg-green-600 text-white">
                          Active ({user.subscription.days_remaining} days)
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded text-sm bg-red-600 text-white">
                          Inactive
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Mobile Actions */}
                  {!user.is_admin && !user.admin_role && (
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => updateUserStatus(user.user_id, !user.active)}
                          className={`px-3 py-2 rounded text-sm font-medium ${
                            user.active 
                              ? 'bg-red-600 hover:bg-red-700 text-white' 
                              : 'bg-green-600 hover:bg-green-700 text-white'
                          }`}
                        >
                          {user.active ? 'Disable Account' : 'Enable Account'}
                        </button>
                        
                        <button
                          onClick={() => updateUserSubscription(
                            user.user_id, 
                            !user.subscription?.active,
                            user.subscription?.active ? null : new Date(Date.now() + 30*24*60*60*1000).toISOString()
                          )}
                          className={`px-3 py-2 rounded text-sm font-medium ${
                            user.subscription?.active 
                              ? 'bg-yellow-600 hover:bg-yellow-700 text-white' 
                              : 'bg-blue-600 hover:bg-blue-700 text-white'
                          }`}
                        >
                          {user.subscription?.active ? 'Cancel Subscription' : 'Grant Subscription'}
                        </button>
                      </div>
                      
                      <button
                        className="w-full px-3 py-2 rounded text-sm font-medium bg-red-700 hover:bg-red-800 text-white"
                        onClick={() => confirmDeleteUser(user.user_id, user.email)}
                      >
                        Delete User
                      </button>
                    </div>
                  )}
                  
                  {/* Admin Label for Admin Users */}
                  {(user.is_admin || user.admin_role) && (
                    <div className="mt-3 text-center">
                      <span className="text-purple-400 font-medium text-sm">
                        🔧 Administrator Account
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Pagination */}
            {usersPagination && usersPagination.pages && usersPagination.pages > 1 && (
              <div className="flex justify-center items-center space-x-4">
                <button
                  onClick={() => {
                    const newPage = currentPage - 1;
                    setCurrentPage(newPage);
                    loadUsers(newPage, searchTerm);
                  }}
                  disabled={currentPage === 1}
                  className="bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:cursor-not-allowed px-4 py-2 rounded"
                >
                  Previous
                </button>
                
                <span className="text-gray-400">
                  Page {currentPage} of {usersPagination.pages}
                </span>
                
                <button
                  onClick={() => {
                    const newPage = currentPage + 1;
                    setCurrentPage(newPage);
                    loadUsers(newPage, searchTerm);
                  }}
                  disabled={currentPage === usersPagination.pages}
                  className="bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:cursor-not-allowed px-4 py-2 rounded"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && analytics && (
          <div className="space-y-6">
            <h2 className="text-2xl font-semibold mb-4">Analytics</h2>
            
            {/* Subscription Overview Cards */}
            {analytics.subscription_trends && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-gray-800 p-4 rounded-lg">
                  <div className="text-gray-400 text-sm">Total Users</div>
                  <div className="text-2xl font-bold text-blue-400">{analytics.subscription_trends.total_users || 0}</div>
                </div>
                <div className="bg-gray-800 p-4 rounded-lg">
                  <div className="text-gray-400 text-sm">Active Subs</div>
                  <div className="text-2xl font-bold text-green-400">{analytics.subscription_trends.active_subscriptions || 0}</div>
                </div>
                <div className="bg-gray-800 p-4 rounded-lg">
                  <div className="text-gray-400 text-sm">Conversion</div>
                  <div className="text-2xl font-bold text-yellow-400">{(analytics.subscription_trends.conversion_rate || 0).toFixed(1)}%</div>
                </div>
                <div className="bg-gray-800 p-4 rounded-lg">
                  <div className="text-gray-400 text-sm">Churn Rate</div>
                  <div className="text-2xl font-bold text-red-400">{(analytics.subscription_trends.churn_rate || 0).toFixed(1)}%</div>
                </div>
                <div className="bg-gray-800 p-4 rounded-lg">
                  <div className="text-gray-400 text-sm">LTV</div>
                  <div className="text-2xl font-bold text-purple-400">${(analytics.subscription_trends.ltv || 0).toFixed(0)}</div>
                </div>
              </div>
            )}

            {/* User Growth Chart */}
            <div className="bg-gray-800 p-6 rounded-lg">
              <h3 className="text-lg font-semibold mb-4">User Growth (12 Months)</h3>
              {analytics.user_growth && analytics.user_growth.length > 0 ? (
                <div className="space-y-2">
                  {analytics.user_growth.slice().reverse().map((item, index) => {
                    const maxUsers = Math.max(...analytics.user_growth.map(g => g.new_users || 0), 1);
                    const barWidth = ((item.new_users || 0) / maxUsers) * 100;
                    return (
                      <div key={index} className="flex items-center gap-3">
                        <span className="text-gray-400 text-sm w-20 flex-shrink-0">{item.month}</span>
                        <div className="flex-1 bg-gray-700 rounded-full h-6 relative">
                          {item.new_users > 0 && (
                            <div 
                              className="bg-blue-500 h-6 rounded-full transition-all duration-300 flex items-center justify-end pr-2"
                              style={{ width: `${Math.max(barWidth, 10)}%` }}
                            >
                              <span className="text-xs text-white font-medium">{item.new_users}</span>
                            </div>
                          )}
                        </div>
                        <span className="text-gray-500 text-sm w-8 text-right">{item.new_users || 0}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-gray-500 text-center py-8">No user growth data available</div>
              )}
            </div>

            {/* Revenue Chart */}
            <div className="bg-gray-800 p-6 rounded-lg">
              <h3 className="text-lg font-semibold mb-4">Revenue (12 Months)</h3>
              {analytics.revenue && analytics.revenue.length > 0 ? (
                <div className="space-y-2">
                  {analytics.revenue.slice().reverse().map((item, index) => {
                    const maxRev = Math.max(...analytics.revenue.map(r => r.revenue || 0), 1);
                    const barWidth = ((item.revenue || 0) / maxRev) * 100;
                    return (
                      <div key={index} className="flex items-center gap-3">
                        <span className="text-gray-400 text-sm w-20 flex-shrink-0">{item.month}</span>
                        <div className="flex-1 bg-gray-700 rounded-full h-6 relative">
                          {item.revenue > 0 && (
                            <div 
                              className="bg-green-500 h-6 rounded-full transition-all duration-300 flex items-center justify-end pr-2"
                              style={{ width: `${Math.max(barWidth, 10)}%` }}
                            >
                              <span className="text-xs text-white font-medium">${item.revenue}</span>
                            </div>
                          )}
                        </div>
                        <span className="text-gray-500 text-sm w-12 text-right">${item.revenue || 0}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-gray-500 text-center py-8">No revenue data available</div>
              )}
            </div>

            {/* Subscription Distribution */}
            {analytics.subscription_stats && analytics.subscription_stats.length > 0 && (
              <div className="bg-gray-800 p-6 rounded-lg">
                <h3 className="text-lg font-semibold mb-4">Subscription Distribution</h3>
                <div className="space-y-3">
                  {analytics.subscription_stats.map((stat, index) => {
                    const total = analytics.subscription_stats.reduce((sum, s) => sum + (s.count || 0), 0);
                    const pct = total > 0 ? ((stat.count || 0) / total * 100) : 0;
                    return (
                      <div key={index} className="flex items-center gap-3">
                        <span className="text-gray-400 w-40">
                          {stat._id ? 'Active' : 'Inactive'}
                        </span>
                        <div className="flex-1 bg-gray-700 rounded-full h-6">
                          <div 
                            className={`h-6 rounded-full flex items-center justify-end pr-2 ${stat._id ? 'bg-green-500' : 'bg-gray-500'}`}
                            style={{ width: `${Math.max(pct, 10)}%` }}
                          >
                            <span className="text-xs text-white font-medium">{stat.count}</span>
                          </div>
                        </div>
                        <span className="text-gray-500 text-sm w-12 text-right">{pct.toFixed(0)}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Support Tab */}
        {activeTab === 'support' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <h2 className="text-2xl font-semibold">Support Tickets</h2>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                {/* Email processing status */}
                {lastEmailCheck && (
                  <span className="text-sm text-gray-400">
                    Last check: {lastEmailCheck.toLocaleTimeString()}
                  </span>
                )}
                {/* Manual email check button */}
                <button
                  onClick={processEmails}
                  disabled={emailProcessing}
                  className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white text-sm rounded-lg transition-colors"
                  data-testid="admin-check-emails-button"
                >
                  {emailProcessing ? (
                    <>
                      <span className="animate-spin mr-2">⟳</span>
                      Processing...
                    </>
                  ) : (
                    <>
                      <span className="mr-2">📧</span>
                      Check Emails
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Support Stats as Clickable Filter Buttons - Responsive */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 lg:gap-4">
              {[
                { key: 'all', label: 'Total Tickets', color: 'blue', count: totalTickets },
                { key: 'open', label: 'Open', color: 'red', count: supportStats?.open || 0 },
                { key: 'in_progress', label: 'In Progress', color: 'yellow', count: supportStats?.in_progress || 0 },
                { key: 'resolved', label: 'Resolved', color: 'green', count: supportStats?.resolved || 0 },
                { key: 'closed', label: 'Closed', color: 'gray', count: supportStats?.closed || 0 }
              ].map((stat) => (
                <button
                  key={stat.key}
                  onClick={() => handleFilterChange(stat.key)}
                  className={`p-4 lg:p-6 rounded-lg transition-all duration-200 text-left hover:scale-105 ${
                    ticketFilter === stat.key
                      ? 'bg-gray-700 ring-2 ring-blue-500 shadow-lg'
                      : 'bg-gray-800 hover:bg-gray-700'
                  }`}
                >
                  {/* Count - Updated: Sliders removed */}
                  <div className={`text-2xl font-bold mb-2 ${
                    stat.color === 'blue' ? 'text-blue-400' :
                    stat.color === 'red' ? 'text-red-400' :
                    stat.color === 'yellow' ? 'text-yellow-400' :
                    stat.color === 'green' ? 'text-green-400' :
                    'text-gray-400'
                  }`}>
                    {stat.count}
                  </div>
                  
                  {/* Label */}
                  <div className="text-sm text-gray-400">{stat.label}</div>
                </button>
              ))}
            </div>

            {/* Tickets List - Responsive Design */}
            <div className="bg-gray-800 rounded-lg">
              {/* Desktop Table View */}
              <div className="hidden lg:block">
                <table className="min-w-full divide-y divide-gray-700">
                  <thead className="bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Ticket ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Subject
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-gray-800 divide-y divide-gray-700">
                    {supportTickets.map((ticket) => (
                      <tr key={ticket.ticket_id} className="hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <span className="text-sm font-medium text-blue-400">
                              #{ticket.ticket_id}
                            </span>
                            {/* Show green badge for tickets with new replies */}
                            {ticket.has_new_replies && (
                              <span className="ml-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                                {ticket.unread_user_messages}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-white">{ticket.user_name}</div>
                            <div className="text-sm text-gray-400">{ticket.user_email}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-white max-w-xs truncate">
                            {ticket.subject}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-xs font-medium text-blue-200">
                            {ticket.support_type}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`text-xs font-medium ${
                            ticket.status === 'open' ? 'text-red-400' :
                            ticket.status === 'in_progress' ? 'text-yellow-400' :
                            ticket.status === 'resolved' ? 'text-green-400' :
                            'text-gray-400'
                          }`}>
                            {ticket.status.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                          {formatTicketDate(ticket.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <button
                            onClick={() => openTicketChat(ticket)}
                            className="text-blue-400 hover:text-blue-300 font-medium mr-4"
                          >
                            Chat
                          </button>
                          <button
                            onClick={() => confirmDelete(ticket.ticket_id)}
                            className="text-red-400 hover:text-red-300 font-medium"
                            title="Delete ticket"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="block lg:hidden p-4 space-y-4">
                {supportTickets.map((ticket) => (
                  <div key={ticket.ticket_id} className="bg-gray-700 rounded-lg p-4">
                    {/* Header with Ticket ID and Status */}
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-blue-400">
                          #{ticket.ticket_id}
                        </span>
                        {/* Show green badge for tickets with new replies */}
                        {ticket.has_new_replies && (
                          <span className="ml-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                            {ticket.unread_user_messages}
                          </span>
                        )}
                      </div>
                      <span className={`text-xs font-medium px-2 py-1 rounded ${
                        ticket.status === 'open' ? 'bg-red-900 text-red-200' :
                        ticket.status === 'in_progress' ? 'bg-yellow-900 text-yellow-200' :
                        ticket.status === 'resolved' ? 'bg-green-900 text-green-200' :
                        'bg-gray-900 text-gray-200'
                      }`}>
                        {ticket.status.replace('_', ' ')}
                      </span>
                    </div>
                    
                    {/* Subject */}
                    <div className="mb-2">
                      <div className="text-sm font-medium text-white">{ticket.subject}</div>
                    </div>
                    
                    {/* User Info */}
                    <div className="mb-2">
                      <div className="text-sm text-white">{ticket.user_name}</div>
                      <div className="text-xs text-gray-400">{ticket.user_email}</div>
                    </div>
                    
                    {/* Type and Date */}
                    <div className="flex justify-between items-center mb-3 text-xs text-gray-400">
                      <span className="text-blue-200">{ticket.support_type}</span>
                      <span>{formatTicketDate(ticket.created_at)}</span>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex space-x-3">
                      <button
                        onClick={() => openTicketChat(ticket)}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 px-4 rounded"
                      >
                        Chat
                      </button>
                      <button
                        onClick={() => confirmDelete(ticket.ticket_id)}
                        className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium py-2 px-4 rounded"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {supportTickets.length === 0 && (
                <div className="text-center py-8">
                  <div className="text-gray-400 text-lg">
                    No {ticketFilter === 'all' ? '' : ticketFilter} tickets found
                  </div>
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalTickets > ticketsPerPage && (
              <div className="flex justify-between items-center bg-gray-800 px-6 py-4 rounded-lg">
                <div className="text-sm text-gray-400">
                  Showing {Math.min((ticketPage - 1) * ticketsPerPage + 1, totalTickets)} to {Math.min(ticketPage * ticketsPerPage, totalTickets)} of {totalTickets} tickets
                </div>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => handlePageChange(ticketPage - 1)}
                    disabled={ticketPage === 1}
                    className="bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:cursor-not-allowed px-4 py-2 rounded font-medium"
                  >
                    Previous
                  </button>
                  
                  <div className="flex items-center space-x-2">
                    {Array.from({ length: Math.ceil(totalTickets / ticketsPerPage) }, (_, i) => i + 1)
                      .filter(pageNum => {
                        // Show first page, last page, current page, and pages around current
                        const totalPages = Math.ceil(totalTickets / ticketsPerPage);
                        return pageNum === 1 || 
                               pageNum === totalPages || 
                               Math.abs(pageNum - ticketPage) <= 1;
                      })
                      .map((pageNum, index, array) => (
                        <div key={pageNum} className="flex items-center">
                          {index > 0 && array[index - 1] !== pageNum - 1 && (
                            <span className="text-gray-400 px-2">...</span>
                          )}
                          <button
                            onClick={() => handlePageChange(pageNum)}
                            className={`px-3 py-1 rounded ${
                              ticketPage === pageNum
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                            }`}
                          >
                            {pageNum}
                          </button>
                        </div>
                      ))}
                  </div>
                  
                  <button
                    onClick={() => handlePageChange(ticketPage + 1)}
                    disabled={ticketPage >= Math.ceil(totalTickets / ticketsPerPage)}
                    className="bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:cursor-not-allowed px-4 py-2 rounded font-medium"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

            {/* Ticket Chat Interface */}
            {selectedTicket && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-gray-800 rounded-lg w-full max-w-4xl mx-4 h-[90vh] flex flex-col">
                  {/* Chat Header */}
                  <div className="flex justify-between items-center p-6 border-b border-gray-700">
                    <div>
                      <h3 className="text-xl font-semibold text-white">
                        Ticket #{selectedTicket.ticket_id}
                      </h3>
                      <div className="flex items-center space-x-4 mt-2 text-sm text-gray-400">
                        <span>{selectedTicket.user_name} ({selectedTicket.user_email})</span>
                        <span>•</span>
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          selectedTicket.status === 'open' ? 'bg-red-900 text-red-200' :
                          selectedTicket.status === 'in_progress' ? 'bg-yellow-900 text-yellow-200' :
                          selectedTicket.status === 'resolved' ? 'bg-green-900 text-green-200' :
                          'bg-gray-900 text-gray-200'
                        }`}>
                          {selectedTicket.status?.replace('_', ' ')}
                        </span>
                        <span>•</span>
                        <span>{selectedTicket.support_type}</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      {/* Status Update Dropdown */}
                      <select
                        value={selectedTicket.status || 'open'}
                        onChange={(e) => updateTicketStatus(selectedTicket.ticket_id, e.target.value)}
                        className="bg-gray-700 text-white rounded px-3 py-1 text-sm"
                      >
                        <option value="open">Open</option>
                        <option value="in_progress">In Progress</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                      </select>
                      <button
                        onClick={closeTicketChat}
                        className="text-gray-400 hover:text-white text-2xl"
                      >
                        ×
                      </button>
                    </div>
                  </div>

                  {/* Chat Messages */}
                  <div className="flex-1 overflow-y-auto p-6 space-y-4">
                    {chatLoading ? (
                      <div className="flex justify-center items-center h-32">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                      </div>
                    ) : (
                      <>
                        {/* Initial ticket message */}
                        <div className="flex items-start space-x-3">
                          <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                            {selectedTicket.user_name?.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1">
                            <div className="bg-gray-700 rounded-lg p-4">
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-medium text-white">{selectedTicket.user_name}</span>
                                <span className="text-xs text-gray-400">
                                  {formatTicketDate(selectedTicket.created_at)}
                                </span>
                              </div>
                              <div className="text-sm text-gray-300 mb-2 font-medium">
                                {selectedTicket.subject}
                              </div>
                              <div className="text-white whitespace-pre-line">
                                {selectedTicket.message}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Chat Messages */}
                        {ticketMessages.map((message, index) => (
                          <div key={message.message_id || index} className="flex items-start space-x-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium ${
                              message.sender_type === 'admin' ? 'bg-green-600' : 'bg-blue-600'
                            }`}>
                              {message.sender_name?.charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1">
                              <div className={`rounded-lg p-4 ${
                                message.sender_type === 'admin' ? 'bg-green-900 bg-opacity-30' : 'bg-gray-700'
                              }`}>
                                <div className="flex items-center justify-between mb-2">
                                  <span className="font-medium text-white">
                                    {message.sender_name}
                                    {message.sender_type === 'admin' && (
                                      <span className="ml-2 text-xs bg-green-600 text-white px-2 py-1 rounded">Admin</span>
                                    )}
                                  </span>
                                  <span className="text-xs text-gray-400">
                                    {message.timestamp_formatted || formatTicketDate(message.timestamp)}
                                  </span>
                                </div>
                                <div className="text-white whitespace-pre-line">
                                  {message.message}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                  </div>

                  {/* Message Input */}
                  <div className="p-6 border-t border-gray-700">
                    <div className="flex space-x-4">
                      <div className="flex-1">
                        <textarea
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          placeholder="Type your reply..."
                          className="w-full bg-gray-700 text-white rounded-lg p-3 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                          rows="3"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              sendMessage();
                            }
                          }}
                        />
                      </div>
                      <div className="flex flex-col justify-end">
                        <button
                          onClick={sendMessage}
                          disabled={!newMessage.trim() || sendingMessage}
                          className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                          data-testid="admin-ticket-send-button"
                        >
                          {sendingMessage ? 'Sending...' : 'Send'}
                        </button>
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-gray-400">
                      Press Enter to send, Shift + Enter for new line
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* System Status Tab */}
        {activeTab === 'system' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-semibold mb-4">System Status & Technical Overview</h2>
            
            {systemLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                <p className="text-gray-400">Loading system status...</p>
              </div>
            ) : (
              <>
                {/* System Overview */}
                {systemOverview && (
                  <div className="bg-gray-800 p-6 rounded-lg border border-purple-500 mb-6">
                    <h3 className="text-xl font-semibold mb-4 text-purple-400">🖥️ System Overview</h3>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="bg-gray-700 rounded p-3">
                        <div className="text-sm text-gray-400">Overall Status</div>
                        <div className={`font-bold ${systemOverview.system_health?.overall_status === 'HEALTHY' ? 'text-green-400' : 'text-red-400'}`}>
                          {systemOverview.system_health?.overall_status || 'UNKNOWN'}
                        </div>
                      </div>
                      <div className="bg-gray-700 rounded p-3">
                        <div className="text-sm text-gray-400">CPU Usage</div>
                        <div className="font-bold text-blue-400">{systemOverview.system_health?.cpu_usage || 0}%</div>


                      </div>
                      <div className="bg-gray-700 rounded p-3">
                        <div className="text-sm text-gray-400">Memory Usage</div>
                        <div className="font-bold text-yellow-400">{systemOverview.system_health?.memory_usage || 0}%</div>
                      </div>
                      <div className="bg-gray-700 rounded p-3">
                        <div className="text-sm text-gray-400">Disk Usage</div>
                        <div className="font-bold text-purple-400">{systemOverview.system_health?.disk_usage || 0}%</div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Technical Admin Sections removed entirely */}

                {/* Engine version impact (lazy-loaded, fetches on mount) */}
                <EngineImpactPanel />
              </>
            )}
          </div>
        )}

        {/* Maintenance Tab */}
        {activeTab === 'maintenance' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-semibold mb-4">System Maintenance</h2>

            {/* System Settings (Registration & Banner) */}
            <AdminSettings />
            
            <div className="bg-gray-800 p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-4 flex items-center">
                <span className="text-orange-400 mr-3">🧹</span>
                Account Cleanup
              </h3>
              <p className="text-gray-400 mb-4">
                Clean up inactive user accounts that haven&apos;t been used in 90+ days and don&apos;t have active subscriptions.
              </p>
              <button
                onClick={handleCleanupInactive}
                className="bg-orange-600 hover:bg-orange-700 px-6 py-3 rounded font-semibold transition-colors"
                data-testid="admin-cleanup-inactive-button"
              >
                Cleanup Inactive Accounts
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 border border-gray-600 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-white mb-4">Confirm Deletion</h3>
            <p className="text-gray-300 mb-6">{deleteConfirmation.message}</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleDeleteCancel}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded transition-colors"
                data-testid="admin-delete-cancel-button"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                data-testid="admin-delete-confirm-button"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;